'use client';

import { FC, useEffect, useState } from 'react';
import { KeyTextField, LinkField } from '@prismicio/client';
import Link from 'next/link';
import { createClient } from '@/prismicio';
import { useScreenSize } from '@/hooks';
import { screenSizes } from '@/constants';
import { HeaderNavigationIcon } from '@/types';
import { useMenuState } from '@/context/headerContext';
import { HeaderIcons } from '@/assets';
import useModal from '@/hooks/useModal';
import { MODAL_NAMES } from '@/constants/modals';
import styles from './listItem.module.scss';

interface ListItemProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  link: LinkField | any;
  title: KeyTextField;
  footerStyles?: { link: string; text: string; item: string };
  scrollToSectionNumber: string | number | null;
  type?: string | null;
  icon?: HeaderNavigationIcon;
  activeTab?: number;
}

export const ListItem: FC<ListItemProps> = ({
  link,
  title,
  footerStyles,
  scrollToSectionNumber,
  type,
  icon,
  activeTab,
}) => {
  const [anchor, setAnchor] = useState<string | null>(null);
  const { openModal } = useModal();

  const screenSize = useScreenSize();
  const { toggleMenu } = useMenuState();

  const handleClickLink = () => {
    if (screenSize.width >= screenSizes.desktop) return;

    toggleMenu();
  };

  useEffect(() => {
    if (!scrollToSectionNumber || !link?.type || !link?.uid) return;

    const client = createClient();

    const findPage = async () => {
      try {
        const { data } = await client.getByUID(link.type, link.uid);
        const hasSlices = 'slices' in data;
        const scrollToSection = Number(scrollToSectionNumber) - 1;

        if (hasSlices) setAnchor(data.slices[scrollToSection].id);
      } catch {
        setAnchor(null);
      }
    };

    findPage();
  }, [scrollToSectionNumber, link.type, link.uid]);
  const params = activeTab !== undefined ? `?activeTab=${activeTab}` : '';

  const href =
    scrollToSectionNumber && anchor
      ? `/${link.uid}${params}#${anchor}`
      : `/${link.uid || link.slug || ''}`;

  const renderContent = () => {
    switch (type) {
      case 'unsubscribe':
        return (
          <div
            className={footerStyles?.link || styles.list_item}
            onClick={() => openModal({ name: MODAL_NAMES.unsubscribe })}
            style={{ cursor: 'pointer' }}
          >
            <span className={footerStyles?.text || styles.item_text}>
              {title}
            </span>
          </div>
        );
      case 'disclosure':
        return (
          <div
            onClick={() => openModal({ name: MODAL_NAMES.generalDisclosure })}
            className={footerStyles?.link || styles.list_item}
            style={{ cursor: 'pointer' }}
          >
            <span className={footerStyles?.text || styles.item_text}>
              {title}
            </span>
          </div>
        );
      case 'ca-privacy-notice':
        return (
          <div
            onClick={() =>
              openModal({ name: MODAL_NAMES.technicalCaliforniaPolicy })
            }
            className={footerStyles?.link || styles.list_item}
            style={{ cursor: 'pointer' }}
          >
            <span className={footerStyles?.text || styles.item_text}>
              {title}
            </span>
          </div>
        );
      case 'terms-of-service':
        return (
          <div
            onClick={() =>
              openModal({ name: MODAL_NAMES.technicalTermsOfService })
            }
            className={footerStyles?.link || styles.list_item}
            style={{ cursor: 'pointer' }}
          >
            <span className={footerStyles?.text || styles.item_text}>
              {title}
            </span>
          </div>
        );
      case 'advertiser-disclosure':
        return (
          <div
            onClick={() =>
              openModal({ name: MODAL_NAMES.advertiserDisclosure })
            }
            className={footerStyles?.link || styles.list_item}
            style={{ cursor: 'pointer' }}
          >
            <span className={footerStyles?.text || styles.item_text}>
              {title}
            </span>
          </div>
        );
      case 'privacy-policy':
        return (
          <div
            onClick={() =>
              openModal({ name: MODAL_NAMES.technicalPrivacyPolicy })
            }
            className={footerStyles?.link || styles.list_item}
            style={{ cursor: 'pointer' }}
          >
            <span className={footerStyles?.text || styles.item_text}>
              {title}
            </span>
          </div>
        );
      case 'consent':
        return (
          <Link
            className={footerStyles?.link || styles.list_item}
            href="/privacy/online-privacy-policy#choice"
          >
            <span className={footerStyles?.text || styles.item_text}>
              {title}
            </span>
          </Link>
        );
      case 'privacy':
        return (
          <Link
            className={footerStyles?.link || styles.list_item}
            href={`/privacy${href}`}
          >
            <span className={footerStyles?.text || styles.item_text}>
              {title}
            </span>
          </Link>
        );
      default:
        return (
          <Link className={footerStyles?.link || styles.list_item} href={href}>
            <span className={footerStyles?.text || styles.item_text}>
              {icon && <HeaderIcons icon={icon} />}
              {title}
            </span>
          </Link>
        );
    }
  };

  return (
    <li
      onClick={handleClickLink}
      className={`${styles.item} ${footerStyles?.item}`}
    >
      {renderContent()}
    </li>
  );
};
